import React from "react";
import {
  OnboardingStep,
  OnboardingData,
} from "@/hooks/use-onboarding";
import { CountryPicker } from "@/components/onboarding/country-picker";
import { UsernameInput } from "@/components/onboarding/username-input";
import { StarterTypePicker } from "@/components/onboarding/starter-type-picker";
import { StarterReveal } from "@/components/onboarding/starter-reveal";
import { IntroView } from "./intro-view";
import { RPGConversationLayout } from "@/components/onboarding/rpg-conversation-layout";
import { getCountryName } from "@/data/countries";
import { useUser } from "@/providers/user-provider";

interface OnboardingViewProps {
  step: OnboardingStep;
  nextStep: () => void;
  previousStep: () => void;
  retry: () => void;
  data: OnboardingData;
  updateData: (newData: Partial<OnboardingData>) => void;
  isLoading?: boolean;
  error?: string | null;
  pickStarter: (types: string[]) => Promise<void>;
  createPlayer: (username: string) => Promise<boolean>;
  verifyUsername: (username: string) => Promise<boolean>;
  updateCountry: (country: string) => Promise<void>;
  completeOnboarding: () => Promise<void>;
}

export const OnboardingView: React.FC<OnboardingViewProps> = ({
  step,
  nextStep,
  previousStep,
  retry,
  data,
  updateData,
  isLoading,
  error,
  pickStarter,
  createPlayer,
  verifyUsername,
  updateCountry,
  completeOnboarding,
}) => {
  const { reloadProfile } = useUser();
  const [currentRevealIndex, setCurrentRevealIndex] = React.useState(0);

  const onSelectStarter = async (types: string[]) => {
    updateData({ starterTypes: types });
    await pickStarter(types);
    nextStep();
  };

  const onRevealComplete = () => {
    if (data.starters && currentRevealIndex < data.starters.length - 1) {
      setCurrentRevealIndex((prev) => prev + 1);
      return;
    }
    void completeOnboarding().then(() => reloadProfile());
  };

  const renderStep = () => {
    if (isLoading) {
      return (
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-pk-blue-3 border-t-transparent rounded-full animate-spin" />
          <p className="text-white font-black italic animate-pulse uppercase tracking-widest">
            Processing...
          </p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex flex-col items-center gap-6 p-8 bg-red-900/40 backdrop-blur-md rounded-3xl border border-red-500/50 max-w-md w-full text-center">
          <h2 className="text-2xl font-black italic text-white uppercase">
            Error
          </h2>
          <p className="text-red-200 font-medium">{error}</p>
          <button
            onClick={retry}
            className="px-8 py-3 bg-white text-red-900 font-black rounded-xl uppercase tracking-widest"
          >
            Retry
          </button>
        </div>
      );
    }

    switch (step) {
      case "country_picker":
        return (
          <RPGConversationLayout
            text="In your world, where are you from?"
            onBack={previousStep}
          >
            <CountryPicker
              onSelect={async (country) => {
                updateData({ country });
                await updateCountry(country);
                nextStep();
              }}
            />
          </RPGConversationLayout>
        );

      case "username_input":
        return (
          <RPGConversationLayout
            text="Before anything else, how should I call you?"
            onBack={previousStep}
          >
            <UsernameInput
              verifyUsername={verifyUsername}
              onComplete={async (username) => {
                updateData({ username });
                const success = await createPlayer(username);
                if (success) {
                  nextStep();
                }
              }}
            />
          </RPGConversationLayout>
        );

      case "celebi_welcome_back":
        return (
          <RPGConversationLayout
            onAdvance={nextStep}
            text={`Alright ${data.username} from ${getCountryName(
              data.country || "",
            )}, nice to meet you. You are going to be a valuable ally in this war.`}
          />
        );

      case "celebi_gift_intro":
        return (
          <RPGConversationLayout
            onAdvance={nextStep}
            text="But before I can introduce you to the road ahead, there is one thing yet to be done. Come back here, once you have 8 allies on your team. Your first two partners will be a gift from me."
          />
        );

      case "starter_picker":
        return (
          <div className="w-full h-full flex flex-col items-center justify-center p-2 bg-pk-night-2">
            <div className="mb-8 text-center">
              <h2 className="text-xl   font-black text-pk-yellow-1 mb-2 uppercase italic">
                A Gift from Celebi
              </h2>
              <p className="text-pk-gray-3 text-xs">
                Select your affinity to reveal your first partner
              </p>
            </div>
            <StarterTypePicker onSelect={onSelectStarter} />
          </div>
        );

      case "starter_reveal":
        const currentStarter = data.starters?.[currentRevealIndex];
        const isLastStarter =
          currentRevealIndex === (data.starters?.length || 0) - 1;

        return (
          <div className="w-full h-full bg-pk-night-2">
            <StarterReveal
              key={`reveal-${currentRevealIndex}`}
              pokemonName={currentStarter?.base_pokemon_name || "Unknown"}
              activeSkills={currentStarter?.active_skills}
              onComplete={onRevealComplete}
              confirmText={isLastStarter ? "Begin Journey" : "Continue"}
            />
          </div>
        );

      case "finished":
        return (
          <div className="flex flex-col items-center gap-4">
            <div className="w-12 h-12 border-4 border-pk-blue-3 border-t-transparent rounded-full animate-spin" />
            <p className="text-white font-black italic uppercase tracking-widest">
              Entering the world...
            </p>
          </div>
        );

      case "intro":
        return <IntroView nextStep={nextStep} />;

      default:
        return <div>Unknown step: {step}</div>;
    }
  };

  return (
    <main
      className="w-full h-full  bg-pk-night-2 flex items-center justify-center relative overflow-hidden"
      style={{
        height: "min(100svh, calc(480px * 18 / 9))",
        maxWidth: "480px",
      }}
    >
      <div className="relative z-10 w-full h-full flex justify-center items-center">
        {renderStep()}
      </div>
    </main>
  );
};
